import { lightAllocationMax, lightDraft, lightStory, lightTier, TIERS, TALENTS, type LightSave, type Talent } from './lastLight';
import { FAILURE_COPY, NAME_MAX, clientName, createLeaderboardClient, createPlayerStore, createRecordStore, createSubmitGuard, rankLine, runKey, type LeaderboardClient, type Player, type PlayerStore, type RecordStore, type SubmitGuard } from './leaderboard';
import './expedition.css';
import './lastLight.css';
const node = (tag: string, text = '', cls = '') => { const el = document.createElement(tag); el.textContent = text; el.className = cls; return el; };
const button = (text: string, action: () => void) => { const b = document.createElement('button'); b.type = 'button'; b.textContent = text; b.onclick = action; return b; };
export function mountLightEntry() {
  const entry = node('section', '', 'expedition-entry');
  entry.append(node('h2', 'OPERATION LAST LIGHT'), node('p', 'Build the barracks. Reinforce the invasion. Keep enough soldiers to break through.', 'expedition-entry-hint'), button('PLAY LAST LIGHT', () => { location.href = '?mode=last-light'; }));
  document.getElementById('start')?.prepend(entry);
}
export function createLightUi(actions: { continue(): void; deploy(t: Talent | null): void; allocate(n: number): void; restart(): void }, world: WorldDeps = defaultWorldDeps()) {
  const panel = node('div', '', 'expedition-overlay light-overlay'); panel.id = 'light-panel'; panel.hidden = true;
  panel.setAttribute('role', 'dialog'); panel.setAttribute('aria-modal', 'true'); panel.setAttribute('aria-label', 'Operation Last Light command post');
  const content = node('div', '', 'expedition-content'); panel.append(content); document.body.append(panel);
  const status = node('div', '', 'expedition-status'); status.id = 'light-status'; document.body.append(status);
  panel.onkeydown = e => { if (e.key !== 'Tab') return; const bs = [...panel.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled), summary')].filter(b => b.getClientRects().length > 0); if (e.shiftKey && document.activeElement === bs[0]) { e.preventDefault(); bs.at(-1)?.focus(); } else if (!e.shiftKey && document.activeElement === bs.at(-1)) { e.preventDefault(); bs[0]?.focus(); } };
  return {
    busy(value: boolean) {
      content.setAttribute('aria-busy', String(value));
      for (const control of content.querySelectorAll<HTMLButtonElement>('button')) control.disabled = value;
    },
    status(s: LightSave) {
      const tier = lightTier(s);
      status.textContent = `LAST LIGHT · TIER ${tier.tier}/3 ${tier.name.toUpperCase()} · ${Math.min(50, s.home - tier.start)}/50 ASSIGNED · SCORE ${s.run.score}`;
    },
    show(s: LightSave, persistent: boolean) {
      const tier = lightTier(s);
      panel.hidden = false; content.replaceChildren();
      content.append(node('h2', s.run.status === 'complete' ? 'OPERATION SECURED' : s.run.status === 'lost' ? 'SQUAD LOST' : 'OPERATION LAST LIGHT', 'expedition-title'), node('p', lightStory(s), 'expedition-intro'));
      const tiers = node('ol', '', 'light-tiers');
      tiers.setAttribute('aria-label', 'Three-tier invasion plan');
      for (const step of TIERS) {
        const completed = s.home >= step.target;
        const item = node('li', '', completed ? 'secured' : '');
        if (!completed && tier.tier === step.tier) item.setAttribute('aria-current', 'step');
        item.append(node('strong', `Tier ${step.tier} · ${step.name}`), node('span', `${Math.max(0, Math.min(50, s.home - step.start))}/50 assigned${completed ? ' · SECURED' : ''}`));
        tiers.append(item);
      }
      content.append(tiers, node('p', `Mission ${s.run.status === 'briefing' ? 2 : s.run.depth} · ${s.run.loadout.n} soldiers · Score ${s.run.score} · Best ${s.best}`, 'expedition-facts'));
      if (s.run.depth === 1 && s.run.status === 'debrief') {
        content.append(button('CONTINUE TO LEVEL 2', actions.continue));
        content.querySelector<HTMLButtonElement>('button')?.focus();
        return;
      }
      if (s.run.status === 'briefing') content.prepend(node('h2', 'LEVEL 2 BRIEFING'));
      if (s.run.status === 'camp') {
        content.append(node('p', 'Choose one field doctrine for this deployment. Doctrines stack until the squad is lost or the operation ends. A/D, arrows or drag to steer; firing is automatic.', 'expedition-intro'));
        const choices = node('div', '', 'light-choices');
        for (const id of lightDraft(s)) { const t = TALENTS.find(t => t.id === id)!; const b = button('', () => actions.deploy(id)); b.append(node('strong', t.name), node('span', t.text)); choices.append(b); }
        if (!lightDraft(s).length) choices.append(button('DEPLOY WITH ALL SIX DOCTRINES', () => actions.deploy(null)));
        content.append(choices);
      } else if (s.run.status === 'debrief' || s.run.status === 'briefing') {
        const max = lightAllocationMax(s);
        content.append(node('p', `Bridge secured. ${tier.objective} Dispatch up to ${max} soldiers now; each leaves your squad and earns 20 points. At least 20 stay with you.`, 'expedition-intro'));
        const choices = node('div', '', 'light-choices');
        for (const n of [...new Set([0, Math.min(20, max), max])]) choices.append(button(n ? `ASSIGN ${n} TO ${tier.name.toUpperCase()} · ${s.run.loadout.n - n} FIGHT ON` : 'KEEP THE SQUAD · FIGHT ON', () => actions.allocate(n)));
        content.append(choices);
      } else if (s.run.status === 'lost' || s.run.status === 'complete') {
        content.append(node('p', s.run.status === 'complete' ? 'The invasion corridor is open. Begin a new operation with all three objectives reset; your earned starting upgrades and records remain.' : 'The barracks and reinforcements already dispatched remain in place. Muster a fresh squad to continue this operation; field doctrines reset.', 'expedition-intro'), worldRecordBlock(s, world), button(s.run.status === 'complete' ? 'BEGIN NEW OPERATION' : s.run.depth === 1 ? 'RETRY' : 'MUSTER NEW SQUAD', actions.restart));
      }
      const intel = document.createElement('details'); intel.className = 'light-intel';
      if (s.run.depth > 1 || s.run.status === 'briefing') {
        const modes = node('details');
        modes.append(node('summary', 'Other game modes'),
          button('CAMPAIGN', () => { location.href = '?level=2'; }),
          button('EXPEDITION', () => { location.href = '?mode=expedition'; }));
        if (world.onWorldRecord) modes.append(button('WORLD RECORD', world.onWorldRecord));
        content.append(modes);
      }
      intel.append(node('summary', 'Army upgrades, field doctrines & service record'));
      for (const step of TIERS) intel.append(node('p', `${s.unlockedTier >= step.tier ? 'UNLOCKED' : `Complete tier ${step.tier}`} · ${step.unlock}`));
      intel.append(node('p', 'Starting upgrades apply when mustering a new squad. Score: 100 × mission plus up to 1,000 surviving soldiers per victory; 20 per soldier dispatched.'));
      intel.append(node('p', `${s.operations} operations secured · ${s.attempts} squads mustered. Records stay in this browser.`));
      if (s.run.talents.length) intel.append(node('p', `Field doctrines: ${s.run.talents.map(t => TALENTS.find(a => a.id === t)!.name).join(' · ')}`));
      if (s.history.length) { const history = node('ol', '', 'light-history'); for (const h of s.history) history.append(node('li', `${h.score} points · mission ${h.depth} · ${h.outcome}`)); intel.append(history); }
      content.append(intel);
      if (!persistent) content.append(node('p', 'Browser storage is unavailable. Progress stays in this tab’s URL; bookmark it to keep your command post.', 'expedition-warning'));
      content.append(button('CLASSIC CAMPAIGN', () => { location.href = '?mode=campaign'; }));
      content.querySelector('button')?.focus();
    },
  };
}

/** Plan 34 slice 2: the world-record deps, injectable so the smoke/unit paths stay offline. */
export interface WorldDeps {
  client(): LeaderboardClient;
  players: PlayerStore;
  /** True once per run key, so a re-render can never submit the same run twice. */
  guard: SubmitGuard;
  /** runKey -> the line that run settled on (the rank, or the failure copy). */
  results: Map<string, string>;
  /** Slice 3: the WORLD RECORD panel opener, wired from main.ts (start entry + the in-game mode list). */
  onWorldRecord?: () => void;
  /** Slice 3: the last accepted submission — the panel's own-row key. */
  records: RecordStore;
}

export function defaultWorldDeps(): WorldDeps {
  let client: LeaderboardClient | null = null;
  return {
    client: () => (client ??= createLeaderboardClient()),
    players: createPlayerStore(),
    guard: createSubmitGuard(),
    results: new Map(),
    records: createRecordStore(),
  };
}

/**
 * The end-of-run submit block. `show()` re-renders the whole panel from state, so the block is
 * rebuilt every time — the settled line and the guard live in `WorldDeps`, not in the DOM, and
 * nothing here can throw into the end screen (the client folds every failure into `{ ok: false }`).
 */
export function worldRecordBlock(s: LightSave, deps: WorldDeps): HTMLElement {
  const key = runKey(s.run);
  const section = node('section', '', 'light-world');
  // The live region is attached EMPTY at build time: screen readers announce a text CHANGE
  // inside an existing region, not text that arrives with the node.
  const live = node('p', '', 'light-world-result');
  live.setAttribute('aria-live', 'polite');
  const show = (text: string) => { live.textContent = text; };
  section.append(node('h3', 'WORLD RECORD', 'light-world-title'), live);
  const settled = deps.results.get(key);
  if (settled) { show(settled); return section; }

  const hint = node('p', `Ranked against every operator. Your best stays in this browser: ${s.best}.`, 'light-world-hint');
  const start = button('SUBMIT TO WORLD RECORD', () => openForm());
  section.append(hint, start);

  function openForm() {
    start.remove(); hint.remove();
    const owner = deps.players.read();
    const wrap = node('div', '', 'light-world-form');
    const input = document.createElement('input');
    input.type = 'text';
    input.maxLength = NAME_MAX;
    input.value = owner.name ?? '';
    input.className = 'expedition-seed-input';
    input.setAttribute('aria-label', 'World record callsign');
    input.autocomplete = 'off';
    input.spellcheck = false;
    const send = button('LOG THIS OPERATION', () => void submit(owner, input.value));
    const sync = () => { send.disabled = clientName(input.value) === null; };
    input.oninput = sync;
    sync();
    wrap.append(input, send);
    section.append(wrap);
    input.focus();

    async function submit(who: Player, raw: string) {
      const name = clientName(raw);
      if (!name || !deps.guard.claim(key)) return;
      deps.players.remember({ id: who.id, name });
      wrap.setAttribute('aria-busy', 'true');
      input.disabled = true; send.disabled = true;
      const result = await deps.client()
        .submit({ clientId: who.id, name, score: s.run.score, depth: s.run.depth, outcome: s.run.status === 'complete' ? 'Operation secured' : 'Squad lost' })
        .catch(() => ({ ok: false as const }));
      if (result.ok) deps.records.remember({ clientId: who.id, name, score: s.run.score, depth: s.run.depth, outcome: s.run.status === 'complete' ? 'Operation secured' : 'Squad lost' }, result.rank, result.total);
      const text = result.ok ? rankLine(result.rank, result.total) : FAILURE_COPY;
      deps.results.set(key, text);
      wrap.remove();
      show(text);
    }
  }
  return section;
}
